import { KeyConfigurations } from '@enums/KeyConfigurations';
import { useAppSelector } from '@hooks/useAppSelector';
import { Slider } from '@shared/atoms';
import { SystemButton } from '@shared/molecules';
import React, { useEffect, useRef, useState } from 'react';
import { RootState } from 'store';
import styles from './index.module.scss';

interface QuestionResponseScaleOption {
  id: number;
  scaleId: number;
  optionText: string | null;
  score: number;
  orderId: number;
  createdAt: string;
  updatedAt: string;
}

interface QuestionResponseScale {
  id: number;
  scale: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  questionnairesV2QuestionResponseScaleOptions: QuestionResponseScaleOption[];
}

interface Question {
  id: number;
  questionnaireId: number;
  responseScaleId: number;
  questionText: string;
  orderId: number;
  required: boolean;
  icon: string | null;
  color: string | null;
  buttonText: string | null;
  description: string | null;
  isBrakeOff: boolean;
  createdAt: string;
  updatedAt: string;
  label: string;
  questionnairesV2QuestionResponseScales: QuestionResponseScale;
}

export const CheckIn = ({ handleStep }: CheckInProps) => {
  const wizardData = useAppSelector((state: RootState) => state.shortStarWizard);
  const formRef = useRef<HTMLFormElement>(null);
  const [announcement, setAnnouncement] = useState<string>('');

  // Sample dataset as provided
  const questions: Question[] = [
    {
      id: 1,
      questionnaireId: 1,
      responseScaleId: 1,
      questionText: 'Mental clarity and emotional balance',
      orderId: 1,
      required: false,
      icon: null,
      color: '#06bf97',
      buttonText: null,
      description: 'My mental clarity and emotional balance',
      isBrakeOff: false,
      createdAt: '2025-03-14T08:17:41.807Z',
      updatedAt: '2025-03-14T08:17:41.807Z',
      label: 'Mental',
      questionnairesV2QuestionResponseScales: {
        id: 1,
        scale: '10-point Scale',
        description: 'Scale from 1 to 10',
        createdAt: '2025-03-14T06:54:54.270Z',
        updatedAt: '2025-03-14T06:54:54.270Z',
        questionnairesV2QuestionResponseScaleOptions: [
          {
            id: 1,
            scaleId: 1,
            optionText: null,
            score: 1,
            orderId: 1,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 2,
            scaleId: 1,
            optionText: null,
            score: 2,
            orderId: 2,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 3,
            scaleId: 1,
            optionText: null,
            score: 3,
            orderId: 3,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 4,
            scaleId: 1,
            optionText: null,
            score: 4,
            orderId: 4,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 5,
            scaleId: 1,
            optionText: null,
            score: 5,
            orderId: 5,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 6,
            scaleId: 1,
            optionText: null,
            score: 6,
            orderId: 6,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 7,
            scaleId: 1,
            optionText: null,
            score: 7,
            orderId: 7,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 8,
            scaleId: 1,
            optionText: null,
            score: 8,
            orderId: 8,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 9,
            scaleId: 1,
            optionText: null,
            score: 9,
            orderId: 9,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 10,
            scaleId: 1,
            optionText: null,
            score: 10,
            orderId: 10,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
        ],
      },
    },
    {
      id: 2,
      questionnaireId: 1,
      responseScaleId: 1,
      questionText: 'Energy levels and physical health',
      orderId: 2,
      required: false,
      icon: null,
      color: '#beaaf3',
      buttonText: null,
      description: 'My energy levels and physical health',
      isBrakeOff: false,
      createdAt: '2025-03-14T08:17:41.807Z',
      updatedAt: '2025-03-14T08:17:41.807Z',
      label: 'Physical',
      questionnairesV2QuestionResponseScales: {
        id: 1,
        scale: '10-point Scale',
        description: 'Scale from 1 to 10',
        createdAt: '2025-03-14T06:54:54.270Z',
        updatedAt: '2025-03-14T06:54:54.270Z',
        questionnairesV2QuestionResponseScaleOptions: [
          {
            id: 1,
            scaleId: 1,
            optionText: null,
            score: 1,
            orderId: 1,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 2,
            scaleId: 1,
            optionText: null,
            score: 2,
            orderId: 2,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 3,
            scaleId: 1,
            optionText: null,
            score: 3,
            orderId: 3,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 4,
            scaleId: 1,
            optionText: null,
            score: 4,
            orderId: 4,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 5,
            scaleId: 1,
            optionText: null,
            score: 5,
            orderId: 5,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 6,
            scaleId: 1,
            optionText: null,
            score: 6,
            orderId: 6,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 7,
            scaleId: 1,
            optionText: null,
            score: 7,
            orderId: 7,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 8,
            scaleId: 1,
            optionText: null,
            score: 8,
            orderId: 8,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 9,
            scaleId: 1,
            optionText: null,
            score: 9,
            orderId: 9,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 10,
            scaleId: 1,
            optionText: null,
            score: 10,
            orderId: 10,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
        ],
      },
    },
    {
      id: 3,
      questionnaireId: 1,
      responseScaleId: 1,
      questionText: 'Financial security and stability',
      orderId: 3,
      required: false,
      icon: null,
      color: '#ff8a4a',
      buttonText: null,
      description: 'My financial security and stability',
      isBrakeOff: false,
      createdAt: '2025-03-14T08:17:41.807Z',
      updatedAt: '2025-03-14T08:17:41.807Z',
      label: 'Financial',
      questionnairesV2QuestionResponseScales: {
        id: 1,
        scale: '10-point Scale',
        description: 'Scale from 1 to 10',
        createdAt: '2025-03-14T06:54:54.270Z',
        updatedAt: '2025-03-14T06:54:54.270Z',
        questionnairesV2QuestionResponseScaleOptions: [
          {
            id: 1,
            scaleId: 1,
            optionText: null,
            score: 1,
            orderId: 1,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 2,
            scaleId: 1,
            optionText: null,
            score: 2,
            orderId: 2,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 3,
            scaleId: 1,
            optionText: null,
            score: 3,
            orderId: 3,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 4,
            scaleId: 1,
            optionText: null,
            score: 4,
            orderId: 4,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 5,
            scaleId: 1,
            optionText: null,
            score: 5,
            orderId: 5,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 6,
            scaleId: 1,
            optionText: null,
            score: 6,
            orderId: 6,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 7,
            scaleId: 1,
            optionText: null,
            score: 7,
            orderId: 7,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 8,
            scaleId: 1,
            optionText: null,
            score: 8,
            orderId: 8,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 9,
            scaleId: 1,
            optionText: null,
            score: 9,
            orderId: 9,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 10,
            scaleId: 1,
            optionText: null,
            score: 10,
            orderId: 10,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
        ],
      },
    },
    {
      id: 4,
      questionnaireId: 1,
      responseScaleId: 1,
      questionText: 'Connections with others and sense of belonging',
      orderId: 4,
      required: false,
      icon: null,
      color: '#019df2',
      buttonText: null,
      description: 'My connections with others and sense of belonging',
      isBrakeOff: false,
      createdAt: '2025-03-14T08:17:41.807Z',
      updatedAt: '2025-03-14T08:17:41.807Z',
      label: 'Social',
      questionnairesV2QuestionResponseScales: {
        id: 1,
        scale: '10-point Scale',
        description: 'Scale from 1 to 10',
        createdAt: '2025-03-14T06:54:54.270Z',
        updatedAt: '2025-03-14T06:54:54.270Z',
        questionnairesV2QuestionResponseScaleOptions: [
          {
            id: 1,
            scaleId: 1,
            optionText: null,
            score: 1,
            orderId: 1,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 2,
            scaleId: 1,
            optionText: null,
            score: 2,
            orderId: 2,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 3,
            scaleId: 1,
            optionText: null,
            score: 3,
            orderId: 3,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 4,
            scaleId: 1,
            optionText: null,
            score: 4,
            orderId: 4,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 5,
            scaleId: 1,
            optionText: null,
            score: 5,
            orderId: 5,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 6,
            scaleId: 1,
            optionText: null,
            score: 6,
            orderId: 6,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 7,
            scaleId: 1,
            optionText: null,
            score: 7,
            orderId: 7,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 8,
            scaleId: 1,
            optionText: null,
            score: 8,
            orderId: 8,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 9,
            scaleId: 1,
            optionText: null,
            score: 9,
            orderId: 9,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
          {
            id: 10,
            scaleId: 1,
            optionText: null,
            score: 10,
            orderId: 10,
            createdAt: '2025-03-14T06:54:57.709Z',
            updatedAt: '2025-03-14T06:54:57.709Z',
          },
        ],
      },
    },
  ];

  const [ratings, setRatings] = useState<Record<number, number>>(
    questions.reduce((acc, question) => {
      const defaultValue = 7;

      return { ...acc, [question.id]: defaultValue };
    }, {}),
  );

  const handleSliderChange =
    (questionId: number, questionLabel: string) => (event: Event, newValue: number | number[]) => {
      const valueAsNumber = newValue as number;
      setRatings((prev) => ({
        ...prev,
        [questionId]: valueAsNumber,
      }));

      setAnnouncement(`${questionLabel} value set to ${valueAsNumber}`);
    };

  useEffect(() => {
    if (formRef.current && questions.length > 0) {
      const firstSlider = formRef.current.querySelector(`[id^="slider-"]`);
      if (firstSlider) {
        (firstSlider as HTMLElement).focus();
      }
    }
  }, [questions.length]);

  const defaultColors = ['#9966FF', '#019DF2', '#33CC66', '#FF9933', '#CC66FF'];

  const handleSubmit = () => {
    Object.entries(ratings).forEach(([questionId, score]) => {
      console.log(`Question ID: ${questionId}, Score: ${score}`);
    });

    setAnnouncement('Your metrics have been saved');
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === KeyConfigurations.ARROW_DOWN || event.key === KeyConfigurations.TAB) {
      const nextSlider = formRef.current?.querySelector(`#slider-${index + 1}`);
      if (nextSlider && event.key === KeyConfigurations.ARROW_DOWN) {
        event.preventDefault();
        (nextSlider as HTMLElement).focus();
      }
    } else if (event.key === KeyConfigurations.ARROW_UP) {
      const prevSlider = formRef.current?.querySelector(`#slider-${index - 1}`);
      if (prevSlider) {
        event.preventDefault();
        (prevSlider as HTMLElement).focus();
      }
    }
  };

  return (
    <div className={styles.container} role="region" aria-label="Daily Check-in Form">
      <form className={styles.card} ref={formRef} aria-live="polite">
        <h2 id="check-in-title" className={styles.title}>
          How are you feeling today?
        </h2>

        <div aria-live="assertive" className="visually-hidden" aria-atomic="true">
          {announcement}
        </div>

        {questions.map((question, index) => {
          const color = question.color || '#0072b0';
          const options =
            question.questionnairesV2QuestionResponseScales
              .questionnairesV2QuestionResponseScaleOptions;
          const minValue = Math.min(...options.map((opt) => opt.score));
          const maxValue = Math.max(...options.map((opt) => opt.score));

          const minOption = options.find((opt) => opt.score === minValue);
          const maxOption = options.find((opt) => opt.score === maxValue);

          const minLabel = minOption?.optionText || minValue.toString();
          const maxLabel = maxOption?.optionText || maxValue.toString();

          const sliderId = `slider-${index}`;
          const questionId = `question-${question.id}`;
          const currentValue = ratings[question.id];

          return (
            <div
              key={question.id}
              className={styles.category}
              role="group"
              aria-labelledby={questionId}
            >
              <div className={styles.categoryHeader}>
                <div className={styles.categoryInfo}>
                  <h3 id={questionId} className={styles.categoryTitle} style={{ color }}>
                    {question.label}
                  </h3>
                  <p id={`${questionId}-desc`} className={styles.categoryDescription}>
                    {question.description || question.questionText}
                  </p>
                </div>
                <div
                  aria-live="polite"
                  aria-atomic="true"
                  className={styles.ratingBadge}
                  style={{ backgroundColor: color }}
                >
                  {currentValue}
                </div>
              </div>
              <Slider
                id={sliderId}
                sliderValue={ratings[question.id]}
                handleSliderChange={handleSliderChange(question.id, question.label)}
                marks
                minValue={minValue}
                maxValue={maxValue}
                colorCode="#505050"
                backgroundColor={color}
                valueLabel="off"
                aria-labelledby={questionId}
                aria-describedby={`${questionId}-desc range-label-${question.id}`}
                onKeyDown={(e) => handleKeyDown(e, index)}
                tabIndex={0}
                role="slider"
                aria-valuenow={currentValue}
                aria-valuemin={minValue}
                aria-valuemax={maxValue}
                aria-orientation="horizontal"
              />
              <div className={styles.rangeLabels} id={`range-label-${question.id}`}>
                <span>{minLabel}</span>
                <span>{maxLabel}</span>
              </div>
            </div>
          );
        })}

        <div className={styles.buttonContainer}>
          <SystemButton onClick={handleSubmit} fullWidthButton>
            Save Today&apos;s Metrics
          </SystemButton>
        </div>
      </form>
    </div>
  );
};

CheckIn.defaultProps = {};

interface CheckInProps {
  handleStep: () => void;
}
